import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, ArrowLeft } from 'lucide-react';
import api, { formatError } from '../lib/api';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      if (data.reset_token) {
        setToken(data.reset_token);
      } else {
        toast.success('If that email exists, a reset link has been generated.');
      }
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const resetUrl = token ? `${window.location.origin}/reset-password?token=${token}` : '';

  const copy = () => {
    navigator.clipboard.writeText(resetUrl);
    toast.success('Reset link copied');
  };

  return (
    <div className="min-h-screen grid-pattern bg-[#F4F4F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-zinc-900 shadow-brutal p-8">
        <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">RECOVERY</div>
          <h2 className="font-display text-3xl font-black tracking-tighter">Forgot password</h2>
        </div>

        {!token ? (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-xs text-zinc-600">Enter your email. We&apos;ll generate a one-time reset link valid for 60 minutes.</p>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Email</label>
              <input
                data-testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-3 bg-white"
              />
            </div>
            <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088] disabled:opacity-60 transition-colors">
              {loading ? 'Generating...' : 'Generate Reset Link →'}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-400 p-3 text-xs text-yellow-900">
              <strong>No email integration yet.</strong> Copy the link below and send it to the person who needs to reset (or use it yourself). The link expires in 60 minutes.
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Reset Link</label>
              <div className="flex items-stretch gap-0 border border-zinc-900">
                <input data-testid="reset-link" readOnly value={resetUrl} className="flex-1 px-3 py-2 text-xs font-mono bg-white outline-none" />
                <button onClick={copy} data-testid="copy-reset-link" className="bg-zinc-900 text-white px-3 hover:bg-zinc-700"><Copy size={14} /></button>
              </div>
            </div>
            <button onClick={() => navigate(`/reset-password?token=${token}`)} data-testid="use-now-btn" className="w-full bg-[#002FA7] text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088] transition-colors">
              Use this link now →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
